/**
 * Reads and writes behind closing a job out — issue #835, ADR 0273.
 *
 * Everything the pop-up says is worked out by `closeoutReview` in
 * packages/core; this only fetches what it reads and calls the three database
 * functions that move a job (`send_job_to_closeout`, `close_job`,
 * `reopen_job`). Nothing here adds up money (CLAUDE.md rule 5).
 */

import type {
  CloseoutInput,
  CloseoutKey,
  CloseoutOption,
  CloseoutQuickbooks,
  Job,
  JobChecklistItem,
} from "@roof-hawk/core";

import { fetchJobBalance, fetchLadderInputs } from "./queries";
import { supabase, unwrap } from "./supabase";

/** One job sent to be closed, as the boards read it. */
export interface CloseoutRow {
  job_id: string;
  sent_by: string | null;
  sent_at: string;
  warnings: { key: string; text: string }[] | null;
}

/**
 * Every job sent to be closed, by job id.
 *
 * Its own request, and forgiving: a database that does not have the table
 * yet must not take the job lists down with it (2026-08-18). The boards then
 * simply show nothing as sent.
 */
export async function fetchCloseouts(): Promise<Map<string, CloseoutRow>> {
  const { data, error } = await supabase
    .from("job_closeouts")
    .select("job_id,sent_by,sent_at,warnings")
    .limit(10000);
  if (error) {
    console.warn("Close-outs could not be read:", error.message);
    return new Map();
  }
  return new Map(
    ((data ?? []) as CloseoutRow[]).map((row) => [row.job_id, row]),
  );
}

/** Everything `closeoutReview` reads for one job. */
export async function fetchCloseoutInput(
  job: Job,
  checklist: readonly JobChecklistItem[],
): Promise<CloseoutInput> {
  const [inputs, balance, invoice, bills, payments, receipts, waiting] =
    await Promise.all([
      fetchLadderInputs([job.id]),
      fetchJobBalance(job).catch(() => null),
      supabase
        .from("quickbooks_invoices")
        .select(
          "state,quickbooks_doc_number,amount_cents,sent_amount_cents,last_error",
        )
        .eq("job_id", job.id)
        .maybeSingle(),
      supabase
        .from("quickbooks_bills")
        .select(
          "source_kind,source_id,state,amount_cents,quickbooks_doc_number,last_error",
        )
        .eq("job_id", job.id),
      supabase
        .from("quickbooks_payments")
        .select("amount_cents,state")
        .eq("job_id", job.id),
      supabase
        .from("quickbooks_receipts")
        .select("vendor_name,amount_cents,txn_date")
        .eq("job_id", job.id)
        .eq("state", "assigned"),
      // Waiting for somebody to say which job they are on. The review keeps
      // the ones whose note names this customer (issue #899).
      supabase
        .from("quickbooks_receipts")
        .select("vendor_name,description,amount_cents,txn_date")
        .eq("state", "new")
        .limit(1000),
    ]);

  // The full rows come back; the ladder's input type names only what the
  // ladder reads, and the review reads a little more of the same rows.
  const ladderOptions = (inputs.get(job.id) ??
    []) as unknown as CloseoutOption[];
  const options = await withSupplierNames(ladderOptions);

  const crewIds = [
    ...new Set(
      options
        .flatMap((o) => o.workOrders)
        .map((wo) => wo.crew_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const crewTrades: Record<string, string[]> = {};
  if (crewIds.length > 0) {
    const rows = unwrap<
      { crew_id: string; trade: { name: string | null } | null }[]
    >(
      await supabase
        .from("crew_trades")
        .select("crew_id, trade:trades(name)")
        .in("crew_id", crewIds),
    );
    for (const row of rows) {
      const name = row.trade?.name;
      if (!name) continue;
      (crewTrades[row.crew_id] ??= []).push(name);
    }
  }

  // Not `unwrap`: it turns "no row" into [], which is truthy and read as an
  // invoice with nothing in it, so a job with none said "undefined" instead
  // of "There is no QuickBooks invoice".
  if (invoice.error) throw new Error(invoice.error.message);
  const inv = (invoice.data ?? null) as {
    state: string;
    quickbooks_doc_number: string | null;
    amount_cents: number | null;
    sent_amount_cents: number | null;
    last_error: string | null;
  } | null;

  const quickbooks: CloseoutQuickbooks = {
    invoice: inv
      ? {
          state: inv.state,
          docNumber: inv.quickbooks_doc_number,
          amountCents: inv.amount_cents,
          sentAmountCents: inv.sent_amount_cents,
          lastError: inv.last_error,
        }
      : null,
    bills: unwrap<
      {
        source_kind: CloseoutQuickbooks["bills"][number]["sourceKind"];
        source_id: string;
        state: string;
        amount_cents: number | null;
        quickbooks_doc_number: string | null;
        last_error: string | null;
      }[]
    >(bills).map((b) => ({
      sourceKind: b.source_kind,
      sourceId: b.source_id,
      state: b.state,
      amountCents: b.amount_cents,
      docNumber: b.quickbooks_doc_number,
      lastError: b.last_error,
    })),
    payments: unwrap<{ amount_cents: number; state: string }[]>(payments).map(
      (p) => ({ amountCents: Number(p.amount_cents ?? 0), state: p.state }),
    ),
    receipts: unwrap<
      {
        vendor_name: string | null;
        amount_cents: number | null;
        txn_date: string | null;
      }[]
    >(receipts).map((r) => ({
      vendor: r.vendor_name,
      amountCents: r.amount_cents,
      txnDate: r.txn_date,
    })),
    waitingReceipts: unwrap<
      {
        vendor_name: string | null;
        description: string | null;
        amount_cents: number | null;
        txn_date: string | null;
      }[]
    >(waiting).map((r) => ({
      vendor: r.vendor_name,
      description: r.description,
      amountCents: r.amount_cents,
      txnDate: r.txn_date,
    })),
  };

  return {
    job: {
      jobType: job.job_type,
      place: job,
      work_orders_internal: job.work_orders_internal,
      materials_internal: job.materials_internal,
      material_by_crew: job.material_by_crew,
      customer: job.contact ?? null,
    },
    checklist,
    options,
    balance,
    quickbooks,
    crewTrades,
  };
}

/**
 * Each material order with its supplier's name, which the review prints.
 *
 * `fetchLadderInputs` hands orders back with only `supplier_id`: the ladder
 * never reads the name, and every job list goes through it. So the names are
 * looked up here, in one request, rather than widening that query. Without
 * this every order read "no supplier" (review of #837).
 */
async function withSupplierNames(
  options: readonly CloseoutOption[],
): Promise<CloseoutOption[]> {
  type Order = CloseoutOption["materialOrders"][number] & {
    supplier_id?: string | null;
  };
  const ids = [
    ...new Set(
      options
        .flatMap((o) => o.materialOrders as readonly Order[])
        .filter((mo) => !mo.supplier?.name)
        .map((mo) => mo.supplier_id)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  if (ids.length === 0) return [...options];
  const rows = unwrap<{ id: string; name: string | null }[]>(
    await supabase.from("suppliers").select("id,name").in("id", ids),
  );
  const names = new Map(rows.map((row) => [row.id, row.name]));
  return options.map((option) => ({
    ...option,
    materialOrders: (option.materialOrders as readonly Order[]).map((mo) =>
      !mo.supplier?.name && mo.supplier_id && names.has(mo.supplier_id)
        ? { ...mo, supplier: { name: names.get(mo.supplier_id) ?? null } }
        : mo,
    ),
  }));
}

/** The one write behind Send. Refused by the database unless it is right. */
export async function sendJobToCloseout(
  jobId: string,
  confirmations: readonly CloseoutKey[],
  warnings: readonly { key: string; text: string }[],
): Promise<void> {
  const { error } = await supabase.rpc("send_job_to_closeout", {
    p_job: jobId,
    p_confirmations: confirmations,
    p_warnings: warnings,
  });
  if (error) throw new Error(error.message);
}

/** Whether the summary email went, written on the record afterwards. */
export async function noteCloseoutEmail(
  jobId: string,
  problem: string | null,
): Promise<void> {
  await supabase
    .from("job_closeouts")
    .update(
      problem
        ? { email_error: problem.slice(0, 1000) }
        : { emailed_at: new Date().toISOString(), email_error: null },
    )
    .eq("job_id", jobId);
}

export async function closeJob(jobId: string): Promise<void> {
  const { error } = await supabase.rpc("close_job", { p_job: jobId });
  if (error) throw new Error(error.message);
}

export async function reopenJob(jobId: string): Promise<void> {
  const { error } = await supabase.rpc("reopen_job", { p_job: jobId });
  if (error) throw new Error(error.message);
}

/** Who finished jobs go to. Null hides the tab and sends nothing. */
export async function setCloseoutProfile(
  profileId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("company_settings")
    .update({ closeout_profile_id: profileId })
    .eq("id", 1);
  if (error) throw new Error(error.message);
}

/** When each closed job was closed, and by whom, off its Closed tick. */
export async function fetchClosedTicks(
  jobIds: readonly string[],
): Promise<Map<string, { at: string | null; by: string | null }>> {
  const out = new Map<string, { at: string | null; by: string | null }>();
  if (jobIds.length === 0) return out;
  const rows = unwrap<
    {
      job_id: string;
      completed_at: string | null;
      completed_by: string | null;
    }[]
  >(
    await supabase
      .from("job_checklist_items")
      .select("job_id,completed_at,completed_by")
      .in("job_id", jobIds as string[])
      .eq("label", "Closed")
      .eq("status", "done"),
  );
  for (const row of rows) {
    const seen = out.get(row.job_id);
    // A job with both a production and a follow-up Closed: the later one.
    if (!seen || (row.completed_at ?? "") > (seen.at ?? ""))
      out.set(row.job_id, { at: row.completed_at, by: row.completed_by });
  }
  return out;
}
